import React, { useRef, useEffect, useState } from "react";
import * as d3 from "d3";

type BoxPlotProps = {
    data: number[];
    width?: number;
    height?: number;
    margin?: { top: number; right: number; bottom: number; left: number };
    useContainerWidth?: boolean;
    boxColor?: string;
    medianColor?: string;
    whiskerColor?: string;
    outlierColor?: string;
};

const defaultMargin = { top: 10, right: 20, bottom: 30, left: 20 };

const BoxPlot: React.FC<BoxPlotProps> = ({
    data,
    width = 600,
    height = 120,
    margin = defaultMargin,
    useContainerWidth = false,
    boxColor = "#bbb",
    medianColor = "#333",
    whiskerColor = "#666",
    outlierColor = "#d62728",
}) => {
    const svgRef = useRef<SVGSVGElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [containerWidth, setContainerWidth] = useState<number>(width);

    // Handle container width measurement and resize
    useEffect(() => {
        if (!useContainerWidth || !containerRef.current) return;

        const updateWidth = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth);
            }
        };

        // Initial measurement
        updateWidth();

        // Add resize observer
        const resizeObserver = new ResizeObserver(updateWidth);
        resizeObserver.observe(containerRef.current);

        return () => resizeObserver.disconnect();
    }, [useContainerWidth]);

    // Use containerWidth when useContainerWidth is true, otherwise use width prop
    const effectiveWidth = useContainerWidth ? containerWidth : width;

    useEffect(() => {
        if (!data || data.length === 0) {
            d3.select(svgRef.current).selectAll("*").remove();
            return;
        }

        const values = data.filter((d) => typeof d === "number" && !isNaN(d)).slice();
        if (values.length === 0) return;
        values.sort(d3.ascending);

        const q1 = d3.quantile(values, 0.25) ?? values[0];
        const median = d3.quantile(values, 0.5) ?? values[Math.floor(values.length / 2)];
        const q3 = d3.quantile(values, 0.75) ?? values[values.length - 1];
        const iqr = q3 - q1;
        const lowerFence = q1 - 1.5 * iqr;
        const upperFence = q3 + 1.5 * iqr;
        const whiskerLow = d3.min(values.filter((v) => v >= lowerFence)) ?? values[0];
        const whiskerHigh = d3.max(values.filter((v) => v <= upperFence)) ?? values[values.length - 1];
        const outliers = values.filter((v) => v < lowerFence || v > upperFence);

        const innerWidth = effectiveWidth - margin.left - margin.right;
        const innerHeight = height - margin.top - margin.bottom;

        const x = d3.scaleLinear().domain([d3.min(values) ?? 0, d3.max(values) ?? 1]).nice().range([0, innerWidth]);

        const svg = d3.select(svgRef.current);
        svg.attr("viewBox", `0 0 ${effectiveWidth} ${height}`).attr("width", effectiveWidth).attr("height", height);

        svg.selectAll("*").remove();

        const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

        const axis = d3.axisBottom(x).ticks(6).tickSizeOuter(0);
        g.append("g")
            .attr("transform", `translate(0, ${innerHeight - 10})`)
            .call(axis)
            .selectAll("text")
            .style("font-size", "10px");

        const centerY = (innerHeight - 10) / 2;

        g.append("line")
            .attr("x1", x(whiskerLow))
            .attr("x2", x(q1))
            .attr("y1", centerY)
            .attr("y2", centerY)
            .attr("stroke", whiskerColor)
            .attr("stroke-width", 2);

        g.append("line")
            .attr("x1", x(q3))
            .attr("x2", x(whiskerHigh))
            .attr("y1", centerY)
            .attr("y2", centerY)
            .attr("stroke", whiskerColor)
            .attr("stroke-width", 2);

        const capHeight = 12;
        g.append("line")
            .attr("x1", x(whiskerLow))
            .attr("x2", x(whiskerLow))
            .attr("y1", centerY - capHeight / 2)
            .attr("y2", centerY + capHeight / 2)
            .attr("stroke", whiskerColor)
            .attr("stroke-width", 2);

        g.append("line")
            .attr("x1", x(whiskerHigh))
            .attr("x2", x(whiskerHigh))
            .attr("y1", centerY - capHeight / 2)
            .attr("y2", centerY + capHeight / 2)
            .attr("stroke", whiskerColor)
            .attr("stroke-width", 2);

        const boxHeight = 30;
        g.append("rect")
            .attr("x", x(q1))
            .attr("y", centerY - boxHeight / 2)
            .attr("width", Math.max(1, x(q3) - x(q1)))
            .attr("height", boxHeight)
            .attr("fill", boxColor)
            .attr("stroke", whiskerColor);

        g.append("line")
            .attr("x1", x(median))
            .attr("x2", x(median))
            .attr("y1", centerY - boxHeight / 2)
            .attr("y2", centerY + boxHeight / 2)
            .attr("stroke", medianColor)
            .attr("stroke-width", 3);

        g.selectAll("circle.outlier")
            .data(outliers)
            .enter()
            .append("circle")
            .attr("class", "outlier")
            .attr("cx", (d: any) => x(d))
            .attr("cy", centerY)
            .attr("r", 3.5)
            .attr("fill", outlierColor)
            .attr("opacity", 0.9);

        g.append("text")
            .attr("x", x(d3.min(values) ?? 0))
            .attr("y", centerY - boxHeight / 2 - 6)
            .attr("font-size", 10)
            .attr("fill", "#ffffff")
            .text((d3.min(values) ?? 0).toString());

        g.append("text")
            .attr("x", x(d3.max(values) ?? 0))
            .attr("y", centerY - boxHeight / 2 - 6)
            .attr("font-size", 10)
            .attr("fill", "#ffffff")
            .attr("text-anchor", "end")
            .text((d3.max(values) ?? 0).toString());
    }, [data, effectiveWidth, height, margin, boxColor, medianColor, whiskerColor, outlierColor]);

    return (
        <div ref={containerRef} style={{ width: useContainerWidth ? '100%' : 'auto' }}>
            <svg ref={svgRef} />
        </div>
    );
};

export default BoxPlot;